'use client'
import { useState } from 'react'
import { cn } from '@/core/lib/utils/cn'
import { Category } from '@/features/categories/model/category.model'
import { useEffect } from 'react'

type Props = {
  categories: Category[]
  selected?: number[]
  onSelect: (ids: number | number[]) => void
  fetchChildren: (parentId: number) => Promise<Category[]>
}

export function CategoryFilter({
  categories,
  selected = [],
  onSelect,
  fetchChildren,
}: Props) {
  const [childrenMap, setChildrenMap] = useState<Record<number, Category[]>>({})
  const [loading, setLoading] = useState<number | null>(null)

  const handleParentClick = async (catId: number) => {
    const isSelected = selected.includes(catId)

    if (isSelected) {
      const children = childrenMap[catId] || []
      const idsToRemove = [catId, ...children.map(c => c.id)]
      onSelect(idsToRemove)
      return
    }

    onSelect(catId)

    if (!childrenMap[catId]) {
      setLoading(catId)
      const children = await fetchChildren(catId)
      setChildrenMap(prev => ({ ...prev, [catId]: children }))
      setLoading(null)
    }
  }
  useEffect(() => {
    const loadInitialChildren = async () => {
      for (const parentId of selected) {
        const isParent = categories.some(cat => cat.id === parentId)

        if (isParent && !childrenMap[parentId]) {
          setLoading(parentId)
          const children = await fetchChildren(parentId)
          setChildrenMap(prev => ({ ...prev, [parentId]: children }))
          setLoading(null)
        }
      }
    }

    loadInitialChildren()
  }, [selected, categories])

  return (
    <div className='space-y-3'>
      {categories.map(parent => {
        const isSelected = selected.includes(parent.id)
        const children = childrenMap[parent.id] || []
        return (
          <div key={parent.id}>
            <button
              onClick={() => handleParentClick(parent.id)}
              className={cn(
                'rounded-xs px-3 py-1 text-sm ring transition',
                isSelected
                  ? 'ring-mountain-bluebird text-mountain-bluebird ring-2'
                  : 'ring-dim-gray text-dim-gray',
              )}
            >
              {parent.name}
            </button>

            {isSelected && (
              <div className='mt-2 ml-4 flex flex-wrap gap-2'>
                {loading === parent.id && (
                  <span className='text-xs'>Loading...</span>
                )}

                {children.map(child => (
                  <button
                    key={child.id}
                    onClick={() => onSelect(child.id)}
                    className={cn(
                      'cursor-pointer rounded-xs px-2 py-1 text-xs ring transition',
                      selected.includes(child.id)
                        ? 'ring-mountain-bluebird text-mountain-bluebird ring-2'
                        : 'ring-dim-gray text-dim-gray',
                    )}
                  >
                    {child.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
